/**
 * ProgressIndicator Component
 * 
 * Displays current status with spinner/icon and message.
 * Part of the json-render component system.
 */

import {
    Loader2,
    CheckCircle2,
    XCircle,
    GitBranch,
    Search,
    Wrench,
    CheckSquare,
    Clock,
} from 'lucide-react';

import type { ProgressIndicatorProps } from '../../types/json-render';

const statusConfig: Record<ProgressIndicatorProps['status'], {
    icon: React.ComponentType<{ className?: string }>;
    color: string;
    animate?: boolean;
}> = {
    cloning: { icon: GitBranch, color: 'text-blue-500', animate: true },
    analyzing: { icon: Search, color: 'text-purple-500', animate: true },
    working: { icon: Wrench, color: 'text-primary', animate: true },
    verifying: { icon: CheckSquare, color: 'text-amber-500', animate: true },
    waiting: { icon: Clock, color: 'text-muted-foreground' },
    completed: { icon: CheckCircle2, color: 'text-green-600' },
    failed: { icon: XCircle, color: 'text-destructive' },
};

export default function ProgressIndicator({
    status,
    message,
    progress,
}: ProgressIndicatorProps) {
    const safeStatus = (typeof status === 'string' && status in statusConfig)
        ? status as keyof typeof statusConfig
        : 'working';
    const safeMessage = typeof message === 'string' && message.trim().length > 0
        ? message
        : 'Working on your request...';
    const safeProgress = typeof progress === 'number' && Number.isFinite(progress)
        ? Math.max(0, Math.min(100, progress))
        : undefined;

    const config = statusConfig[safeStatus];
    const Icon = config.icon;

    return (
        <div className="flex items-center gap-3 py-2">
            {config.animate ? (
                <div className="relative">
                    <Icon className={`h-5 w-5 ${config.color}`} />
                    <Loader2 className={`h-5 w-5 ${config.color} animate-spin absolute top-0 left-0 opacity-30`} />
                </div>
            ) : (
                <Icon className={`h-5 w-5 ${config.color}`} />
            )}

            <div className="flex-1">
                <span className="text-sm">{safeMessage}</span>

                {/* Progress bar if provided */}
                {safeProgress !== undefined && safeProgress > 0 && (
                    <div className="mt-1 h-1.5 bg-muted rounded-full overflow-hidden">
                        <div
                            className="h-full bg-primary transition-all duration-300"
                            style={{ width: `${safeProgress}%` }}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}
